import { getClient } from '../core/global-config.js';
import { createAndWaitTask } from '../core/index.js';

export interface GoogleVeo31Params {
  /**
   * 视频生成提示词，长度必须在1-2000字符之间
   */
  prompt: string;
  /**
   * 视频时长（秒），必须是4、6或8
   * @default 8
   * @values 4 | 6 | 8
   */
  durationSeconds?: 4 | 6 | 8;
  /**
   * 视频宽高比
   * @default "16:9"
   * @values 9:16 | 16:9
   */
  aspectRatio?: '9:16' | '16:9';
  /**
   * 视频分辨率
   * @default "720p"
   * @values 720p | 1080p
   */
  resolution?: '720p' | '1080p';
  /**
   * 压缩质量
   * @default "optimized"
   * @values optimized | lossless
   */
  compressionQuality?: 'optimized' | 'lossless';
  /**
   * 人物生成设置
   * @default "allow_adult"
   * @values dont_allow | allow_adult
   */
  personGeneration?: 'dont_allow' | 'allow_adult';
  /**
   * 生成样本数，范围1-4
   * @default 1
   * @range 1 - 4
   */
  sampleCount?: number;
  /**
   * 随机种子，范围0-4294967295
   */
  seed?: number;
  /**
   * 负面提示词
   */
  negativePrompt?: string;
  /**
   * 是否生成音频
   */
  generateAudio?: boolean;
  /**
   * 是否增强提示词
   */
  enhancePrompt?: boolean;
  /**
   * 存储URI
   */
  storageUri?: string;
  /**
   * 输入图像，URL或Base64编码
   */
  image?: string;
  /**
   * 最后一帧图像，URL或Base64编码
   */
  lastFrame?: string;
  /**
   * 输入视频，URL或Base64编码
   */
  video?: string;
  /**
   * 参考图像列表，最多3个
   */
  referenceImages?: Array<{
    /**
     * 参考图像，URL或Base64编码
     */
    image?: string;
    /**
     * 参考类型
     * @values asset | style
     */
    referenceType?: 'asset' | 'style';
  }>;
}

export interface GoogleVeo31Resource {
  type: string;
  url: string;
  size?: number;
  jobId?: string;
  [key: string]: any;
}

/**
 * google_veo_31
 *
 * Google Veo 3.1 Video Generation
 * 
 * Video generation implementation for Google Vertex AI Veo API.
 * 
 * Model: veo-3.1-generate-preview
 * Features: 文字生成视频 (T2V) 和图像生成视频 (I2V)，支持参考图像
 * Duration: 4、6、8秒
 * Resolution: 720p, 1080p
 * Aspect Ratio: 9:16, 16:9
 * 
 *
 * @example
 * ```typescript
 * import { initSeacloud, googleVeo31 } from 'seacloud-sdk';
 *
 * initSeacloud('your-api-key');
 *
 * const result = await googleVeo31({
 *   // 参数见 GoogleVeo31Params 接口定义
 * });
 *
 * console.log(result); // 返回生成的资源列表
 * ```
 *
 * @param params 请求参数，详见 GoogleVeo31Params 接口定义
 * @returns 生成的资源列表
 */
export async function googleVeo31(
  params: GoogleVeo31Params,
): Promise<GoogleVeo31Resource[]> {
  const client = await getClient();

  const pollingOptions = {
    intervalMs: 3000,
    maxAttempts: 60,
  };

  const result = await createAndWaitTask(
    client,
    '/model/v1/generation',
    {
      model: 'google_veo_31',
      input: [{ params }],
    },
    pollingOptions,
  );

  const resources: GoogleVeo31Resource[] = [];

  if (result.output) {
    for (const item of result.output) {
      if (item.content) {
        for (const resource of item.content) {
          resources.push({
            type: resource.type || 'unknown',
            url: resource.url || '',
            size: resource.size,
            jobId: resource.jobId,
            ...resource,
          });
        }
      }
    }
  }

  return resources;
}
